import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Stethoscope, Save, Loader2, CheckCircle2, HeartHandshake } from "lucide-react";
import axios from "axios";
import { formatApiError } from "@/lib/api";
import { AGA_SECTIONS, CONSULTATION_TYPES, getFormSectionsForType } from "@/lib/aga";
import AGASection from "@/components/AGASection";
import { TID } from "@/lib/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const typeLabel = Object.fromEntries(CONSULTATION_TYPES.map((t) => [t.value, t.label]));

export default function PatientIntake() {
    const { token } = useParams();
    const [state, setState] = useState(null);
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [currentSection, setCurrentSection] = useState(0);

    // Patient-facing sections only — schema varies by consultation type
    const allSections = state ? getFormSectionsForType(state.consultation.consultation_type) : AGA_SECTIONS;
    const sections = allSections.filter((s) => s.forPatient);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await publicApi.get(`/intake/${token}`);
                setState(data);
                setData(data.assessment?.data || {});
                if (data.consultation.status === "intake_completed") setSubmitted(true);
            } catch (e) { setError(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [token]);

    const save = async () => {
        setSaving(true);
        try {
            await publicApi.put(`/intake/${token}`, { data, filled_by: "patient" });
            toast.success("Respostas salvas");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const submit = async () => {
        setSaving(true);
        try {
            await publicApi.put(`/intake/${token}`, { data, filled_by: "patient" });
            await publicApi.post(`/intake/${token}/submit`);
            setSubmitted(true);
            toast.success("Pré-consulta enviada. Obrigado!");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center bg-base"><Loader2 className="animate-spin text-brand" size={32} /></div>;
    if (error) return <ErrorScreen message={error} />;
    if (submitted) return <ThankYouScreen />;

    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
        <div className="min-h-screen bg-base pb-20">
            <header className="bg-white/80 backdrop-blur-xl border-b border-[rgba(30,63,51,0.1)] sticky top-0 z-40">
                <div className="max-w-3xl mx-auto px-4 md:px-8 h-16 flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-brand text-white flex items-center justify-center">
                        <Stethoscope size={18} />
                    </span>
                    <div>
                        <div className="font-display text-brand font-semibold">Pré-consulta</div>
                        <div className="text-xs text-muted-clinical">Clínica Fernandes</div>
                    </div>
                </div>
            </header>

            <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
                <div className="mb-6">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-2">
                        {typeLabel[state.consultation.consultation_type]}
                    </div>
                    <h1 className="font-display text-3xl font-medium text-brand">Olá, {state.patient.full_name || "paciente"}</h1>
                    <p className="text-[var(--text-secondary)] mt-2">
                        Este formulário ajuda o Dr. Marcos e sua equipe a chegarem preparados para sua consulta.
                        Responda o que souber — <strong>não precisa saber tudo</strong>. Sua consulta cobre o restante.
                    </p>
                </div>

                {/* Progress */}
                <div className="mb-6">
                    <div className="flex items-center justify-between text-xs text-muted-clinical mb-1.5">
                        <span>Seção {currentSection + 1} de {sections.length}</span>
                        <span>{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full h-2 bg-alt rounded-full overflow-hidden">
                        <div className="h-full bg-[var(--brand-secondary)] transition-all" style={{ width: `${progress}%` }} />
                    </div>
                </div>

                {/* Section tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {sections.map((s, i) => (
                        <button
                            key={s.id}
                            data-testid={TID.intake.sectionTab(s.id)}
                            onClick={() => setCurrentSection(i)}
                            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${i === currentSection ? "bg-brand text-white" : "bg-alt text-brand-secondary hover:bg-[#EAE4D8]"}`}
                        >
                            {s.title}
                        </button>
                    ))}
                </div>

                <div className="card-earthy mb-6">
                    <AGASection
                        section={section}
                        value={data[section.id] || {}}
                        onChange={(v) => setData({ ...data, [section.id]: v })}
                    />
                </div>

                <div className="flex flex-wrap gap-3 items-center justify-between">
                    <div className="flex gap-2">
                        <button onClick={() => setCurrentSection(Math.max(0, currentSection - 1))} disabled={currentSection === 0} className="btn-secondary">
                            Anterior
                        </button>
                        {currentSection < sections.length - 1 && (
                            <button onClick={() => { save(); setCurrentSection(currentSection + 1); }} className="btn-primary">
                                Próxima
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button data-testid={TID.intake.saveBtn} onClick={save} disabled={saving} className="btn-secondary">
                            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar rascunho
                        </button>
                        {currentSection === sections.length - 1 && (
                            <button data-testid={TID.intake.submitBtn} onClick={submit} disabled={saving} className="btn-primary">
                                <CheckCircle2 size={16} /> Enviar pré-consulta
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ThankYouScreen() {
    return (
        <div className="min-h-screen bg-base flex items-center justify-center px-4">
            <div className="card-earthy max-w-lg text-center">
                <HeartHandshake size={48} className="text-[var(--brand-secondary)] mx-auto mb-4" />
                <h1 className="font-display text-2xl font-medium text-brand mb-2">Recebemos suas informações</h1>
                <p className="text-[var(--text-secondary)]">
                    O Dr. Marcos e sua equipe irão revisar suas respostas antes da consulta. Se quiser complementar algo, você ainda pode reabrir este link.
                </p>
                <Link to="/" className="btn-secondary mt-6">Fechar</Link>
            </div>
        </div>
    );
}

function ErrorScreen({ message }) {
    return (
        <div className="min-h-screen bg-base flex items-center justify-center px-4">
            <div className="card-earthy max-w-md text-center">
                <h1 className="font-display text-xl font-medium text-[var(--status-danger)] mb-2">Link inválido</h1>
                <p className="text-[var(--text-secondary)]">{message}</p>
                <Link to="/" className="btn-secondary mt-6">Voltar</Link>
            </div>
        </div>
    );
}
